import { useContext, useEffect, useSyncExternalStore } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { Modal, ModalActions } from '../Modal';
import { Button } from '../Button';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../../lib/layout-i18n';

/**
 * 이탈 경고 — **등록 · 수정이 있는 화면은 나가기 전에 묻는다** (2026-09-08 사용자 지시).
 *
 * ## ★★★ 왜 화면마다 짜지 않고 공용인가
 *
 * 「쓰던 것이 사라진다」는 **한 화면의 사정이 아니라 제품의 약속**이다. 화면마다 손으로 짜면
 * 어떤 화면은 묻고 어떤 화면은 안 묻는데, 사용자는 그 차이를 미리 알 수 없어 **결국 어느
 * 화면에서도 믿지 못한다.** 그래서 «묻는 방식»을 한 벌로 둔다.
 *
 * ## 막는 길이 둘이다 — 둘 다 막아야 한다
 *
 * ① **앱 안에서 옮기기**(좌측 메뉴 · 뒤로가기 · 링크) — `useBlocker`.
 * ② **브라우저를 닫거나 새로고침** — `beforeunload`.
 *    ★ ②는 우리 문구를 못 쓴다(브라우저가 제 문장을 쓴다) — 그래도 **막는 것 자체가 값**이다.
 *
 * ## ⚠ `useBlocker` 는 «데이터 라우터» 안에서만 산다
 *
 * `createBrowserRouter` 밖(예: 검사의 `MemoryRouter`)에서 부르면 **던진다**. 훅은 조건부로
 * 못 부르므로 **컴포넌트를 둘로 가른다** — 바깥이 라우터 유무를 보고 안쪽을 그릴지 정한다.
 * 그래서 데이터 라우터가 없는 자리에서도 `beforeunload` 만은 그대로 산다.
 *
 * ## 문구
 * 기본값은 `layout.unsaved.*` 다 — **낱말을 화면마다 다시 적지 않는다.** 그 화면만의 사정이
 * 있으면(「보낸 뒤에는 못 고칩니다」 같은) `title`·`description` 으로 덮는다.
 */
export interface UnsavedGuardProps {
  /** 지금 잃을 것이 있는가. 거짓이면 아무것도 안 한다(막지도, 묻지도 않는다). */
  when: boolean;
  /** 창 제목 — 안 주면 공용 문구. */
  title?: string;
  /** 설명 한 줄 — 안 주면 공용 문구. */
  description?: string;
  /** 「나간다」 쪽 이름. */
  leaveLabel?: string;
  /** 「더 쓴다」 쪽 이름. */
  stayLabel?: string;
  /**
   * 같은 경로 안에서 검색값 · 해시만 바뀌는 이동은 막지 않는다 (2026-09-15 · 선택 · 기본 false).
   * 탭을 주소(`?tab=`)에 두는 화면이 탭을 누를 때마다 버리기 창이 뜨지 않게 한다 — 화면은 그대로이고
   * 쓰던 값도 그대로 남기 때문이다. 다른 경로로 나가는 것은 켜도 여전히 묻는다.
   * ★ 기본값(false)이면 종전과 같다 — 같은 경로 이동도 막는다.
   */
  ignoreSamePath?: boolean;
}

/** ② 브라우저를 닫거나 새로고침 — 문구는 브라우저가 정한다(우리 것을 못 쓴다). */
function useBeforeUnloadWarning(when: boolean) {
  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 옛 브라우저는 반환값을 봤다 — 지금은 무시되지만 두어도 해가 없다.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);
}

export function UnsavedGuard(props: UnsavedGuardProps) {
  useBeforeUnloadWarning(props.when);
  // ★ 훅이 아니라 **그릴지 말지**로 가른다 — `useBlocker` 를 조건부로 부를 수는 없다.
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (!dataRouter) return null;
  return <RouteLeaveGuard {...props} />;
}

function RouteLeaveGuard({ when, title, description, leaveLabel, stayLabel, ignoreSamePath = false }: UnsavedGuardProps) {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const blocker = useBlocker(
    ignoreSamePath
      ? ({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname
      : when,
  );

  /**
   * ★ 잃을 것이 없어지면(저장했다 · 보냈다) **막힌 것을 스스로 푼다.**
   *   안 풀면 「보내고 목록으로」가 창 뒤에서 멈춰 선다.
   */
  useEffect(() => {
    // ⚠ reset() 이 아니라 proceed() 다 (2026-10-18 · 메일 「임시 저장」 뒤 화면이 그대로였다).
    //   저장·보내기는 «같은 틱»에 입력을 비우고 이동한다 — 이동을 볼 때는 아직 잃을 것이 있어 막히고,
    //   다음 그리기에서 when 이 false 가 된다. 그때 reset() 은 그 이동을 «취소»해 화면이 멈춰 섰다.
    //   잃을 것이 없어졌으니 막아 둔 이동을 «보낸다».
    if (!when && blocker.state === 'blocked') blocker.proceed();
  }, [when, blocker]);

  if (blocker.state !== 'blocked') return null;

  return (
    <Modal
      size="sm"
      title={title ?? tLayout('layout.unsaved.title')}
      onClose={() => blocker.reset()}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={() => blocker.reset()}>
            {stayLabel ?? tLayout('layout.unsaved.stay')}
          </Button>
          {/* 되돌릴 수 없는 쪽이라 붉게 — 「나가기」가 곧 «버리기» 다. */}
          <Button variant="danger" onClick={() => blocker.proceed()}>
            {leaveLabel ?? tLayout('layout.unsaved.leave')}
          </Button>
        </ModalActions>
      )}
    >
      <p className="wc-unsaved__desc">{description ?? tLayout('layout.unsaved.desc')}</p>
    </Modal>
  );
}
